import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {useCollapsibleSidebarState} from '@github-ui/layout-primitives/use-collapsible-sidebar-state'
import {registerReactPartial} from '@github-ui/react-core/register-partial'
import {ReposIssuesSidebar, type ReposIssuesSidebarProps} from '@github-ui/repos-issues-sidebar-ui/ReposIssuesSidebar'
import {CollapsibleFeedbackLink} from '@github-ui/repos-issues-sidebar-ui/CollapsibleFeedbackLink'

import {SIDEBAR_STORAGE_KEY} from '@github-ui/repos-issues-sidebar-ui/constants'

/**
 * `initialExpanded` is consumed here (forwarded to `useCollapsibleSidebarState`)
 * rather than by `ReposIssuesSidebar`, which reads expanded state from
 * `<CollapsibleSidebar>` context.
 */
type CollapsiblePartialSidebarProps = Omit<ReposIssuesSidebarProps, 'forceHardNavigation'> & {
  initialExpanded?: boolean
}

/**
 * Wraps `ReposIssuesSidebar` for the standalone Rails partial. Provides the
 * `Repository navigation` landmark via `<CollapsibleSidebar ariaLabel>` so
 * AT users still get a labelled landmark.
 */
export function CollapsiblePartialSidebar({initialExpanded, ...props}: CollapsiblePartialSidebarProps) {
  const {isExpanded, toggle} = useCollapsibleSidebarState({
    storageKey: SIDEBAR_STORAGE_KEY,
    initialExpanded,
  })
  return (
    <CollapsibleSidebar
      isExpanded={isExpanded}
      ariaLabel="Repository navigation"
      expandedWidth="small"
      footer={
        <>
          <CollapsibleFeedbackLink />
          <CollapsibleSidebar.Toggle isExpanded={isExpanded} onToggle={toggle} />
        </>
      }
    >
      <ReposIssuesSidebar {...props} forceHardNavigation collapsible />
    </CollapsibleSidebar>
  )
}

registerReactPartial('repos-issues-sidebar', {
  Component: (props: Omit<ReposIssuesSidebarProps, 'forceHardNavigation'>) => {
    return <CollapsiblePartialSidebar {...props} />
  },
})
